<template>
  <section id="server-monitoring" :dir="$functions.currentLangDirection()">
    <b-card>
      <!-- Header -->
      <b-card-title class="mb-2">
        <feather-icon icon="ActivityIcon" size="24" class="align-text-top" />
        مانیتورینگ سرور (Real-Time)
      </b-card-title>

      <!-- Auto Refresh Toggle -->
      <div class="d-flex justify-content-between align-items-center mb-2">
        <div>
          <b-form-checkbox v-model="autoRefresh" switch class="custom-control-success">
            <span class="switch-icon-left"><feather-icon icon="CheckIcon" /></span>
            <span class="switch-icon-right"><feather-icon icon="XIcon" /></span>
            بروزرسانی خودکار ({{ refreshInterval / 1000 }} ثانیه)
          </b-form-checkbox>
        </div>
        <div>
          <small class="text-muted">آخرین بروزرسانی: {{ lastUpdate }}</small>
        </div>
      </div>

      <!-- Stats Cards -->
      <b-row>
        <b-col lg="4" sm="6">
          <b-card class="text-center" bg-variant="success" text-variant="white">
            <h2 class="mb-0">{{ stats.devices.connected }}</h2>
            <p class="mb-0">دستگاه متصل</p>
            <small>از {{ stats.devices.totalRegistered }} دستگاه</small>
          </b-card>
        </b-col>

        <b-col lg="4" sm="6">
          <b-card class="text-center" bg-variant="warning" text-variant="white">
            <h2 class="mb-0">{{ stats.devices.recentlyOnline }}</h2>
            <p class="mb-0">آنلاین اخیر</p>
            <small>در 5 دقیقه گذشته</small>
          </b-card>
        </b-col>

        <b-col lg="4" sm="6">
          <b-card class="text-center" bg-variant="info" text-variant="white">
            <h2 class="mb-0">{{ stats.memory.rss }}</h2>
            <p class="mb-0">مصرف RAM</p>
            <small>Heap: {{ stats.memory.heapUsed }}</small>
          </b-card>
        </b-col>
      </b-row>

      <!-- Tabs -->
      <b-tabs class="mt-2" content-class="mt-3">
        <!-- Tab 1: Connected Devices -->
        <b-tab title="دستگاه‌های متصل" active>
          <b-card-text>
            <!-- راهنمای وضعیت دستگاه -->
            <b-alert show variant="info" class="mb-3">
              <div class="d-flex align-items-center">
                <feather-icon icon="InfoIcon" size="18" class="ml-2" />
                <div>
                  <strong>راهنمای وضعیت‌ها:</strong>
                  <div class="mt-1">
                    <b-badge variant="success" class="mr-2"><feather-icon icon="PowerIcon" size="12" /> روشن</b-badge>
                    <span class="text-muted mr-3">موتور خودرو روشن است</span>

                    <b-badge variant="secondary" class="mr-2"><feather-icon icon="PowerIcon" size="12" /> خاموش</b-badge>
                    <span class="text-muted mr-3">موتور خودرو خاموش است</span>

                    <b-badge variant="info" class="mr-2"><feather-icon icon="BatteryChargingIcon" size="12" /> شارژ</b-badge>
                    <span class="text-muted mr-3">دستگاه در حال شارژ شدن است</span>

                    <b-badge variant="primary" class="mr-2"><feather-icon icon="NavigationIcon" size="12" /> ردیابی فعال</b-badge>
                    <span class="text-muted">ماژول GPS دستگاه فعال و در حال ردیابی است</span>
                  </div>
                </div>
              </div>
            </b-alert>

            <div class="d-flex align-items-center mb-2">
              <b-input-group class="flex-grow-1 ml-2">
                <template #prepend>
                  <b-input-group-text>
                    <feather-icon icon="SearchIcon" />
                  </b-input-group-text>
                </template>
                <b-form-input
                  v-model="deviceSearch"
                  placeholder="جستجوی IMEI یا نام دستگاه..."
                />
              </b-input-group>
              <b-button
                variant="outline-success"
                size="sm"
                :disabled="isExporting || devices.length === 0"
                @click="exportDevices"
              >
                <feather-icon
                  :icon="isExporting ? 'LoaderIcon' : 'DownloadIcon'"
                  size="16"
                  class="align-text-top"
                  :class="{ 'spin-icon': isExporting }"
                />
                {{ isExporting ? 'در حال آماده سازی...' : 'خروجی اکسل' }}
              </b-button>
            </div>

            <v-data-table
              :headers="deviceHeaders"
              :items="filteredDevices"
              :items-per-page="20"
              class="elevation-1"
              :loading="loading"
              loading-text="در حال بارگذاری..."
            >
              <template v-slot:item.lastSeen="{ item }">
                <b-badge :variant="getLastSeenVariant(item.lastSeen)">
                  {{ formatLastSeen(item.lastSeen) }}
                </b-badge>
              </template>

              <template v-slot:item.speed="{ item }">
                {{ item.lastPosition.speed || 0 }} km/h
              </template>

              <template v-slot:item.status="{ item }">
                <div class="d-flex flex-wrap">
                  <b-badge v-if="item.lastStatus.ignition" variant="success" class="mb-1 mr-1">
                    <feather-icon icon="PowerIcon" size="12" /> روشن
                  </b-badge>
                  <b-badge v-else variant="secondary" class="mb-1 mr-1">
                    <feather-icon icon="PowerIcon" size="12" /> خاموش
                  </b-badge>

                  <b-badge v-if="item.lastStatus.charging" variant="info" class="mb-1 mr-1">
                    <feather-icon icon="BatteryChargingIcon" size="12" /> شارژ
                  </b-badge>

                  <b-badge v-if="item.lastStatus.gpsTracking" variant="primary" class="mb-1 mr-1">
                    <feather-icon icon="NavigationIcon" size="12" /> ردیابی فعال
                  </b-badge>
                </div>
              </template>
            </v-data-table>
          </b-card-text>
        </b-tab>

        <!-- Tab 2: Server Stats -->
        <b-tab title="آمار سرور">
          <b-card-text>
            <b-row>
              <b-col md="6">
                <h5>اطلاعات سرور</h5>
                <b-list-group>
                  <b-list-group-item class="d-flex justify-content-between align-items-center">
                    مدت زمان اجرا
                    <b-badge variant="primary" pill>{{ formatUptime(stats.serverUptime) }}</b-badge>
                  </b-list-group-item>
                  <b-list-group-item class="d-flex justify-content-between align-items-center">
                    Heap Used
                    <b-badge variant="info" pill>{{ stats.memory.heapUsed }}</b-badge>
                  </b-list-group-item>
                  <b-list-group-item class="d-flex justify-content-between align-items-center">
                    Heap Total
                    <b-badge variant="info" pill>{{ stats.memory.heapTotal }}</b-badge>
                  </b-list-group-item>
                  <b-list-group-item class="d-flex justify-content-between align-items-center">
                    RSS Memory
                    <b-badge variant="warning" pill>{{ stats.memory.rss }}</b-badge>
                  </b-list-group-item>
                </b-list-group>
              </b-col>

              <b-col md="6">
                <h5>آمار اتصالات</h5>
                <b-list-group>
                  <b-list-group-item class="d-flex justify-content-between align-items-center">
                    دستگاه‌های متصل
                    <b-badge variant="success" pill>{{ stats.devices.connected }}</b-badge>
                  </b-list-group-item>
                  <b-list-group-item class="d-flex justify-content-between align-items-center">
                    کل دستگاه‌ها
                    <b-badge variant="secondary" pill>{{ stats.devices.totalRegistered }}</b-badge>
                  </b-list-group-item>
                </b-list-group>
              </b-col>
            </b-row>
          </b-card-text>
        </b-tab>
      </b-tabs>
    </b-card>
  </section>
</template>

<script>
import {
  BCard,
  BCardTitle,
  BCardText,
  BRow,
  BCol,
  BFormCheckbox,
  BBadge,
  BTabs,
  BTab,
  BInputGroup,
  BInputGroupText,
  BFormInput,
  BButton,
  BListGroup,
  BListGroupItem,
  BAlert,
} from 'bootstrap-vue'
import axios from 'axios'
import DeviceService from '@/services/device.service'

export default {
  name: 'ServerMonitoring',
  components: {
    BCard,
    BCardTitle,
    BCardText,
    BRow,
    BCol,
    BFormCheckbox,
    BBadge,
    BTabs,
    BTab,
    BInputGroup,
    BInputGroupText,
    BFormInput,
    BButton,
    BListGroup,
    BListGroupItem,
    BAlert,
  },
  data() {
    return {
      loading: false,
      isExporting: false,
      autoRefresh: true,
      refreshInterval: 5000, // 5 seconds
      lastUpdate: 'هرگز',
      timer: null,
      deviceSearch: '',
      userSearch: '',
      stats: {
        serverUptime: 0,
        users: {
          online: 0,
          totalSockets: 0,
        },
        devices: {
          connected: 0,
          recentlyOnline: 0,
          totalRegistered: 0,
        },
        memory: {
          heapUsed: '0 MB',
          heapTotal: '0 MB',
          rss: '0 MB',
        },
      },
      devices: [],
      users: [],
      deviceHeaders: [
        { text: 'IMEI', value: 'imei', sortable: true },
        { text: 'نام', value: 'title', sortable: true },
        { text: 'آخرین فعالیت', value: 'lastSeen', sortable: true },
        { text: 'سرعت', value: 'speed', sortable: false },
        { text: 'وضعیت', value: 'status', sortable: false },
      ],
      userHeaders: [
        { text: 'شناسه', value: 'userId', sortable: true },
        { text: 'نام کاربری', value: 'username', sortable: true },
        { text: 'نوع', value: 'type', sortable: true },
        { text: 'تعداد Socket', value: 'socketCount', sortable: true },
        { text: 'دستگاه‌ها', value: 'devices', sortable: false },
      ],
    }
  },
  computed: {
    filteredDevices() {
      if (!this.deviceSearch) return this.devices
      const search = this.deviceSearch.toLowerCase()
      return this.devices.filter(
        d => d.imei.toLowerCase().includes(search) || d.title.toLowerCase().includes(search),
      )
    },
    filteredUsers() {
      if (!this.userSearch) return this.users
      const search = this.userSearch.toLowerCase()
      return this.users.filter(
        u => u.username.toLowerCase().includes(search) || u.userId.toLowerCase().includes(search),
      )
    },
  },
  watch: {
    autoRefresh(val) {
      if (val) {
        this.startAutoRefresh()
      } else {
        this.stopAutoRefresh()
      }
    },
  },
  mounted() {
    this.fetchData()
    if (this.autoRefresh) {
      this.startAutoRefresh()
    }
  },
  beforeDestroy() {
    this.stopAutoRefresh()
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const apiUrl = this.$gpsServers.server1.url

        // Fetch overview stats
        const statsResponse = await axios.get(`${apiUrl}/stats`, {
          headers: this.getAuthHeader()
        })
        this.stats = statsResponse.data

        // Fetch devices list
        const devicesResponse = await axios.get(`${apiUrl}/stats/devices`, {
          headers: this.getAuthHeader()
        })
        this.devices = devicesResponse.data.devices || []

        this.lastUpdate = new Date().toLocaleTimeString('fa-IR')
      } catch (error) {
        console.error('Error fetching monitoring data:', error)
        this.$toast.error('خطا در دریافت اطلاعات مانیتورینگ')
      } finally {
        this.loading = false
      }
    },
    getAuthHeader() {
      const user = JSON.parse(localStorage.getItem('user'))
      if (user && user.token) {
        return { Authorization: `Bearer ${user.token}` }
      }
      return {}
    },
    startAutoRefresh() {
      this.timer = setInterval(() => {
        this.fetchData()
      }, this.refreshInterval)
    },
    stopAutoRefresh() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    formatLastSeen(seconds) {
      if (seconds === null) return 'هرگز'
      if (seconds < 60) return `${seconds} ثانیه پیش`
      if (seconds < 3600) return `${Math.floor(seconds / 60)} دقیقه پیش`
      if (seconds < 86400) return `${Math.floor(seconds / 3600)} ساعت پیش`
      return `${Math.floor(seconds / 86400)} روز پیش`
    },
    formatUptime(seconds) {
      const days = Math.floor(seconds / 86400)
      const hours = Math.floor((seconds % 86400) / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      return `${days}d ${hours}h ${minutes}m`
    },
    getLastSeenVariant(seconds) {
      if (seconds === null) return 'secondary'
      if (seconds < 60) return 'success'
      if (seconds < 300) return 'info'
      if (seconds < 900) return 'warning'
      return 'danger'
    },
    getBatteryVariant(battery) {
      if (!battery) return 'secondary'
      if (battery > 80) return 'success'
      if (battery > 50) return 'info'
      if (battery > 20) return 'warning'
      return 'danger'
    },
    getUserTypeVariant(type) {
      if (type === 0) return 'primary' // Client
      if (type === 1) return 'success' // Reseller
      if (type === 2) return 'danger' // Admin
      return 'secondary'
    },
    getUserTypeLabel(type) {
      if (type === 0) return 'کاربر'
      if (type === 1) return 'نماینده'
      if (type === 2) return 'ادمین'
      return 'نامشخص'
    },
    viewOnMap(device) {
      // TODO: Navigate to map with device location
      console.log('View device on map:', device)
      this.$toast.info(`مشاهده ${device.title} روی نقشه`)
    },
    async exportDevices() {
      this.isExporting = true
      try {
        const res = await DeviceService.getAll({})
        const deviceInfoMap = {}
        if (res && res.result && res.result.data) {
          res.result.data.forEach(d => {
            deviceInfoMap[d.imei] = {
              sim: d.sim || '',
              userMobile: d.userId ? d.userId.mobile : '',
              userName: d.userId ? `${d.userId.name} ${d.userId.family}` : '',
              model: d.modelId ? d.modelId.title : '',
              expireTime: d.expireTime || '',
            }
          })
        }

        const escape = val => {
          const s = String(val === null || val === undefined ? '' : val)
          return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s
        }

        const headers = ['IMEI', 'نام دستگاه', 'نام کاربر', 'شماره موبایل', 'شماره سیم کارت', 'مدل دستگاه', 'تاریخ انقضا', 'آخرین فعالیت', 'سرعت (km/h)', 'استارت', 'شارژ', 'ردیابی GPS', 'نوع اتصال']

        const rows = this.devices.map(device => {
          const info = deviceInfoMap[device.imei] || {}
          return [
            device.imei,
            device.title,
            info.userName || '',
            info.userMobile || '',
            info.sim || '',
            info.model || '',
            info.expireTime || '',
            this.formatLastSeen(device.lastSeen),
            device.lastPosition ? (device.lastPosition.speed || 0) : 0,
            device.lastStatus && device.lastStatus.ignition ? 'روشن' : 'خاموش',
            device.lastStatus && device.lastStatus.charging ? 'بله' : 'خیر',
            device.lastStatus && device.lastStatus.gpsTracking ? 'فعال' : 'غیرفعال',
            device.connectionType || 'TCP',
          ].map(escape).join(',')
        })

        const csvContent = [headers.map(escape).join(','), ...rows].join('\n')
        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv; charset=utf-8' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `connected-devices-${new Date().toLocaleDateString('fa-IR')}.csv`)
        document.body.appendChild(link)
        link.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(link)

        this.$toast.success('فایل گزارش با موفقیت دانلود شد')
      } catch (error) {
        console.error('Export error:', error)
        this.$toast.error('خطا در دانلود گزارش')
      } finally {
        this.isExporting = false
      }
    },
  },
}
</script>

<style scoped>
#server-monitoring {
  min-height: calc(100vh - 200px);
}

.spin-icon {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>